import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import Button from '../Button';
import Hyperlink from '../Hyperlink';
import NavigationCard from '../NavigationCard';
import { drawerDurationMs } from '../utils/cssDuration';
import useSideMenuPush from './useSideMenuPush';
import './SideMenu.css';

/**
 * SideMenu — Figma OVER-LL `Navigation/SideMenu` (2388:6010)
 *
 * A floating panel that slides in from the left, inset 12px from the screen
 * edges: gray-075 header (title + X), then the menu itself —
 *   featured  → one NavigationCard (button) across the full width
 *   cards     → NavigationCards (link), two a row (any count — rows wrap)
 *   links     → plain a.Regular links under a gray-0 rule
 *   actions   → text buttons (icon + label) split by a hairline, in a bar
 *               pinned to the panel's bottom, outside the
 *               scrolling body — phones only by default: on desktop the
 *               header carries the same entries as icons (`actionsOnDesktop`)
 * Every block renders only when its data is passed.
 *
 * Desktop and mobile are ONE component (Resolution rule), the layout switches
 * at 1024px in CSS:
 *   desktop — 480px, floating 12px from the screen edges, rounded
 *   phones  — Figma OVER-LL 2778:29896: flush left, full height, the page is
 *             PUSHED aside by the panel width (`pushTarget`, see
 *             useSideMenuPush) and a handle on the panel's right edge shows
 *             that a swipe to the left closes it (the X stays)
 *
 * Portalled to <body> — the Header that opens it has a backdrop-filter, which
 * would otherwise become the containing block of the fixed panel.
 */
const SideMenu = ({
  isOpen = false,
  onClose,
  title = 'Menu',
  featured,
  cards = [],
  links = [],
  actions = [],
  actionsOnDesktop = false,
  closeOnNavigate = true,
  closeLabel = 'Close',
  pushTarget = '#root',
  preview = false,
  className = '',
  children,
}) => {
  const [isShown, setIsShown] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimer = useRef(null);
  const panelRef = useRef(null);
  const overlayRef = useRef(null);
  const returnFocusRef = useRef(null);
  const titleId = useId();

  const clearDragRef = useRef(null);

  const slideOut = useCallback((then) => {
    // A snapped-back swipe leaves inline styles that would swallow the animation
    clearDragRef.current?.();
    setIsClosing(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setIsClosing(false);
      setIsShown(false);
      then?.();
    }, drawerDurationMs());
  }, []);

  /* X / overlay / Escape / a menu item: slide out, then report */
  const handleClose = useCallback(() => slideOut(onClose), [slideOut, onClose]);

  /* Phones: page push + swipe to close (the swipe already slid it out) */
  const handleSwipeClose = useCallback(() => {
    setIsClosing(false);
    setIsShown(false);
    onClose?.();
  }, [onClose]);

  const { swipeHandlers, clearDrag } = useSideMenuPush({
    active: !preview && isShown,
    closing: isClosing,
    panelRef,
    overlayRef,
    pushTarget,
    onSwipeClose: handleSwipeClose,
  });
  clearDragRef.current = clearDrag;

  /* The parent closing it (`isOpen` → false) slides out too */
  useEffect(() => {
    if (preview) return;
    if (isOpen) {
      clearTimeout(closeTimer.current);
      setIsClosing(false);
      setIsShown(true);
    } else if (isShown && !isClosing) {
      slideOut();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /* Scroll lock, Escape, focus in → focus back to the opener */
  useEffect(() => {
    if (preview || !isOpen) return undefined;

    returnFocusRef.current = document.activeElement;
    panelRef.current?.focus({ preventScroll: true });
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      returnFocusRef.current?.focus?.({ preventScroll: true });
    };
  }, [isOpen, preview, handleClose]);

  /* A menu item: run its own handler, then close (navigation leaves the menu) */
  const itemClick = (item) => (e) => {
    item.onClick?.(e);
    if (closeOnNavigate && !preview) handleClose();
  };

  if (!preview && !isOpen && !isShown) return null;

  const panel = (
    <div
      ref={panelRef}
      className={[
        'ds-side-menu',
        preview && 'ds-side-menu--preview',
        isClosing && 'ds-side-menu--closing',
        className,
      ].filter(Boolean).join(' ')}
      role={preview ? undefined : 'dialog'}
      aria-modal={preview ? undefined : 'true'}
      aria-labelledby={titleId}
      tabIndex={-1}
      {...(preview ? {} : swipeHandlers)}
    >
      {/* Phones: the swipe affordance on the panel's right edge (a tap closes too;
          out of the tab order — the X is the keyboard way out) */}
      {!preview && (
        <button
          type="button"
          className="ds-side-menu__handle"
          onClick={handleClose}
          aria-label={closeLabel}
          tabIndex={-1}
        />
      )}
      <div className="ds-side-menu__header">
        <h2 id={titleId} className="ds-side-menu__title typo-h3">{title}</h2>
        <Button
          size="tertiary"
          variant="gray"
          iconOnly
          iconLeft={<X size={20} strokeWidth={1.75} />}
          onClick={preview ? onClose : handleClose}
          aria-label={closeLabel}
          className="ds-side-menu__close"
        />
      </div>

      <nav className="ds-side-menu__body" aria-labelledby={titleId}>
        {featured && (
          <NavigationCard
            card
            type="button"
            image={featured.image}
            imageAlt={featured.imageAlt}
            imageFit={featured.imageFit}
            imageAspectRatio={featured.imageAspectRatio}
            label={featured.label}
            href={featured.href}
            onClick={itemClick(featured)}
          />
        )}

        {cards.length > 0 && (
          <div className="ds-side-menu__cards">
            {cards.map((card) => (
              <NavigationCard
                key={card.label}
                card
                type="link"
                showArrow={false}
                image={card.image}
                imageAlt={card.imageAlt}
                imageFit={card.imageFit}
                imageAspectRatio={card.imageAspectRatio}
                label={card.label}
                href={card.href}
                onClick={itemClick(card)}
              />
            ))}
          </div>
        )}

        {links.length > 0 && (
          <ul className="ds-side-menu__links">
            {links.map((link) => (
              <li key={link.label}>
                <Hyperlink variant="regular" href={link.href} onClick={itemClick(link)}>
                  {link.label}
                </Hyperlink>
              </li>
            ))}
          </ul>
        )}

        {children}
      </nav>

      {actions.length > 0 && (
        <div
          className={[
            'ds-side-menu__actions',
            actionsOnDesktop && 'ds-side-menu__actions--desktop',
          ].filter(Boolean).join(' ')}
        >
          {actions.map((action, index) => (
            <React.Fragment key={action.label}>
              {/* USPV2's hairline between neighbours */}
              {index > 0 && <span className="ds-side-menu__divider" aria-hidden="true" />}
              <Button
                size="tertiary"
                variant="text"
                iconLeft={action.icon}
                href={action.href}
                onClick={itemClick(action)}
                className="ds-side-menu__action"
              >
                {action.label}
              </Button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );

  if (preview) return panel;

  return createPortal(
    <>
      <div
        ref={overlayRef}
        className={`ds-side-menu__overlay${isClosing ? ' ds-side-menu__overlay--closing' : ''}`}
        onClick={handleClose}
        aria-hidden="true"
        {...swipeHandlers}
      />
      {panel}
    </>,
    document.body,
  );
};

const itemShape = {
  label: PropTypes.string.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
};

const cardShape = {
  ...itemShape,
  image: PropTypes.string.isRequired,
  imageAlt: PropTypes.string,
  imageFit: PropTypes.oneOf(['cover', 'contain']),
  /** Image frame ratio ('3 / 2', '3:2') — default the NavigationCard 276 / 221 */
  imageAspectRatio: PropTypes.string,
};

SideMenu.propTypes = {
  /** Controlled open state */
  isOpen: PropTypes.bool,
  /** Fires after the slide-out — X, overlay, Escape, or a menu item */
  onClose: PropTypes.func,
  /** Header title */
  title: PropTypes.node,
  /** Full-width card with a button */
  featured: PropTypes.shape(cardShape),
  /** Link cards, two a row */
  cards: PropTypes.arrayOf(PropTypes.shape(cardShape)),
  /** Plain links under the rule */
  links: PropTypes.arrayOf(PropTypes.shape(itemShape)),
  /** Text buttons pinned to the panel's bottom, split by a hairline — `icon` is a node */
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.node,
    href: PropTypes.string,
    onClick: PropTypes.func,
  })),
  /** Show `actions` on desktop too (default: phones only — the header has them there) */
  actionsOnDesktop: PropTypes.bool,
  /** Close the menu after a card / link click */
  closeOnNavigate: PropTypes.bool,
  /** Accessible name of the X button (and the phone swipe handle) */
  closeLabel: PropTypes.string,
  /** Phones: CSS selector of the page element the open menu pushes aside (the app root); null = no push */
  pushTarget: PropTypes.string,
  /** Static, in-flow render (no overlay, portal, animation or scroll lock) — the DS dashboard */
  preview: PropTypes.bool,
  /** Extra class on the panel */
  className: PropTypes.string,
  /** Extra content after the links */
  children: PropTypes.node,
};

export default SideMenu;
